/**
 * Canonical SKJV filename composition for studies and groups. Single source of
 * truth shared by the web app's writes and any CLI tooling that ships records
 * straight to Drive.
 *
 * Lives in @skjv/storage so a Node CLI can import the exact same logic the
 * browser uses — no risk of filename format drift between callers.
 */
import { getBookInfo } from '@skjv/common';
export function studyFileBaseName(study) {
    const start = study.startingLocation;
    const end = study.endingLocation;
    const bookInfo = getBookInfo(start.testamentIndex, start.bookIndex);
    const bookAbbr = safe(bookInfo?.abbrev || 'Unknown');
    const chapter = start.chapterIndex + 1;
    const startVerse = start.verseIndex + 1;
    let verseRange = String(startVerse);
    if (end) {
        const endVerse = end.verseIndex + 1;
        const sameBookChapter = end.bookIndex === start.bookIndex && end.chapterIndex === start.chapterIndex;
        if (sameBookChapter && endVerse > startVerse) {
            verseRange = `${startVerse}-${endVerse}`;
        }
        else if (!sameBookChapter) {
            const endBookInfo = getBookInfo(end.testamentIndex, end.bookIndex);
            const endAbbr = safe(endBookInfo?.abbrev || 'Unknown');
            verseRange = `${startVerse}-${endAbbr}_${end.chapterIndex + 1}_${endVerse}`;
        }
    }
    return `SKJV-Study-${bookAbbr}_${chapter}_${verseRange}-${safe(study.name || 'Untitled')}`;
}
/**
 * Human-readable folder name for a study — the ONE per-study folder name used
 * by every hierarchical store (local disk folders and Drive subfolders).
 *
 * Grammar: `<Book>-<ch>_<verse>[-<endRange>]_<kebab-title>` where `-` binds
 * book↔chapter and the range, `_` binds chapter↔verse and joins the title.
 *
 * Examples:
 *   "Ecc-1_1_all-is-vanity"           (single verse)
 *   "Ecc-1_1-5_wisdom-vs-folly"       (range within a chapter)
 *   "Ecc-1_1-2_5_cross-chapter"       (range across chapters)
 *   "Gen-1_1-Rev-22_21_whole-bible"   (range across books)
 *
 * Multi-word abbreviations compact ("1 Sam" → "1Sam") so the reference part
 * never contains spaces.
 */
export function studyFolderName(study) {
    const start = study.startingLocation;
    const end = study.endingLocation;
    const startInfo = getBookInfo(start.testamentIndex, start.bookIndex);
    const startAbbr = compactAbbrev(startInfo?.abbrev || 'Unknown');
    const startCh = start.chapterIndex + 1;
    const startV = start.verseIndex + 1;
    let ref = `${startAbbr}-${startCh}_${startV}`;
    if (end) {
        const endV = end.verseIndex + 1;
        const endCh = end.chapterIndex + 1;
        const sameBook = end.bookIndex === start.bookIndex && end.testamentIndex === start.testamentIndex;
        const sameCh = sameBook && endCh === startCh;
        if (sameCh && endV > startV) {
            ref += `-${endV}`;
        }
        else if (sameBook && !sameCh) {
            ref += `-${endCh}_${endV}`;
        }
        else if (!sameBook) {
            const endInfo = getBookInfo(end.testamentIndex, end.bookIndex);
            const endAbbr = compactAbbrev(endInfo?.abbrev || 'Unknown');
            ref += `-${endAbbr}-${endCh}_${endV}`;
        }
    }
    return `${ref}_${kebab(study.name || 'Untitled')}`;
}
function compactAbbrev(abbrev) {
    return abbrev.replace(/\s+/g, '');
}
function kebab(s) {
    const folded = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    const slug = folded
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/-+$/, '');
    return slug.length > 0 ? slug : 'untitled';
}
export function studyGroupFileBaseName(group) {
    return `SKJV-Group-${safe(group.name || 'Untitled')}`;
}
function safe(s) {
    const trimmed = s.replace(/[\\/:*?"<>|]+/g, '-').trim();
    const collapsed = trimmed.replace(/\s+/g, '_');
    return collapsed.length > 0 ? collapsed.slice(0, 80) : 'untitled';
}
//# sourceMappingURL=study-naming.js.map